// Partner referral capture (client side).
//
// A client scans a partner's flyer sticker → /r/{code}, which persists the
// code here, then lands on signup. business-signup forwards the code into the
// Stripe Checkout metadata, and stripe-webhook opens the attribution. This
// module is the single source of truth for how the code is stored and read so
// the /r landing and Auth.tsx never drift.
//
// First-touch: the FIRST code captured wins for the whole window; a later scan
// does not overwrite it. Attribution ultimately lands on the code in the DB,
// so an unclaimed code still counts and is inherited when a partner claims it.

const STORAGE_KEY = 'workloop_ref';

// Keep in sync with partner_commission_plans.attribution_window_days (default
// 30). This is only the client-side capture horizon; the DB re-checks the real
// window at claim/convert time, so a small mismatch is harmless.
const WINDOW_DAYS = 30;

interface StoredRef {
  code: string;
  ts: number; // epoch ms of first capture
}

function readRaw(): StoredRef | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredRef>;
    if (typeof parsed?.code !== 'string' || typeof parsed?.ts !== 'number') return null;
    return { code: parsed.code, ts: parsed.ts };
  } catch {
    return null;
  }
}

function isFresh(ref: StoredRef): boolean {
  return Date.now() - ref.ts <= WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Persist a referral code (first-touch). No-op if a fresh code is already
 * stored, so the earliest partner keeps the attribution. Safe to call on every
 * /r render. Returns the code that is now in effect.
 */
export function captureReferralCode(rawCode: string | null | undefined): string | null {
  const code = rawCode?.trim().slice(0, 32);
  if (!code) return getStoredReferralCode();

  const existing = readRaw();
  if (existing && isFresh(existing)) return existing.code; // first-touch wins

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, ts: Date.now() } satisfies StoredRef));
  } catch {
    // Private mode / storage disabled — the ?ref= URL param still carries the
    // code through this session; attribution just won't survive a reload.
  }
  return code;
}

/** The active referral code, or null if none / expired (expired is cleared). */
export function getStoredReferralCode(): string | null {
  const ref = readRaw();
  if (!ref) return null;
  if (!isFresh(ref)) {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
  return ref.code;
}

/**
 * Clear the stored code.
 *
 * NOT called on signup any more, deliberately. Auth.tsx used to clear here the
 * moment the account existed — before a single dollar was paid — which orphaned
 * every referred customer who abandoned the Stripe card screen. See the note at
 * that call site. The 30-day window above is now what expires the code, and
 * that is the intended lifetime.
 */
export function clearStoredReferralCode(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
